import { useLayoutEffect, useRef } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import copy from '@/i18n/ns/mobile';
import { useChatCopy } from '@/lib/chat-i18n';
import { installCanvasSheet } from './sheet';

/** Own canvas modality and mobile geometry; navigation and leave guards stay in Workspace. */
export function useCanvasSheet(
  kind: string,
  objectId: string,
  onClose: () => unknown,
  full = false,
  onBack = onClose,
) {
  const sheet = useRef<HTMLElement>(null);
  const scrim = useRef<HTMLButtonElement>(null);
  const handle = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const back = useRef(onBack);
  back.current = onBack;
  useLayoutEffect(() => {
    if (!sheet.current || !scrim.current || !handle.current) return;
    return installCanvasSheet({
      sheet: sheet.current,
      scrim: scrim.current,
      handle: handle.current,
      initialSnap: kind === 'activity' || kind === 'source' ? 0.5 : 1,
      onClose: () => close.current(),
      full,
      onBack: () => back.current(),
    });
  }, [kind, objectId, full]);
  return { sheet, scrim, handle };
}

export function CanvasSheetHandle({ handleRef }: { handleRef: React.Ref<HTMLButtonElement> }) {
  const labels = useNamespaceCopy(copy);
  return (
    <button
      ref={handleRef}
      type="button"
      className="mobile-sheet-handle"
      role="slider"
      aria-label={labels.resizePanel}
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={100}
      aria-keyshortcuts="ArrowUp ArrowDown Home End"
    >
      <span aria-hidden="true" />
    </button>
  );
}

export function CanvasSheetScrim({ scrimRef }: { scrimRef: React.Ref<HTMLButtonElement> }) {
  const labels = useChatCopy();
  return (
    <button
      ref={scrimRef}
      type="button"
      className="mobile-sheet-scrim"
      aria-label={labels.close}
      tabIndex={-1}
      hidden
    />
  );
}
